import {
  CalendarOutlined,
  CheckCircleFilled,
  CloseOutlined,
  LinkOutlined,
  RightOutlined,
  SearchOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Input, Modal, Tag } from "antd";
import { FC, useMemo, useState } from "react";
import {
  PROJECT_PHASE_LABELS,
  PROJECT_PHASES,
  ProjectPhase,
} from "../../projectPhase";
import { groupTasksByDependencyLevel } from "../../taskGraph";
import {
  getTaskOverdueContentCount,
  getTaskOverdueDays,
} from "../../taskDue";
import TaskPriorityTag, { TaskPriority } from "../TaskPriorityTag";
import "./index.css";

type TaskStatus = "todo" | "doing" | "blocked" | "review" | "done" | "discarded";

export interface ProjectTaskOverviewTask {
  id?: string;
  title: string;
  phase: ProjectPhase;
  status: TaskStatus;
  priority?: TaskPriority;
  assignees: string[];
  predecessor_ids: string[];
  due_time?: number;
  subtask_summary: {
    total: number;
    completed: number;
    reviewing: number;
    progress: number;
    overdue_content_count?: number;
    next_content_due_time?: number;
  };
}

interface ProjectTaskOverviewModalProps {
  getMemberName: (userId: string) => string;
  loading: boolean;
  onClose: () => void;
  onOpenPhase: (phase: ProjectPhase) => void;
  onOpenTask: (task: ProjectTaskOverviewTask) => void;
  open: boolean;
  projectName: string;
  tasksByPhase: Record<ProjectPhase, ProjectTaskOverviewTask[]>;
}

const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: "待处理",
  doing: "进行中",
  blocked: "阻塞",
  review: "评审中",
  done: "已完成",
  discarded: "已废弃",
};

function formatDate(timestamp?: number) {
  if (!timestamp) return "未设置截止日";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(timestamp));
}

const ProjectTaskOverviewModal: FC<ProjectTaskOverviewModalProps> = ({
  getMemberName,
  loading,
  onClose,
  onOpenPhase,
  onOpenTask,
  open,
  projectName,
  tasksByPhase,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const normalizedSearchValue = searchValue.trim().toLocaleLowerCase();
  const summary = useMemo(() => {
    const tasks = PROJECT_PHASES.flatMap((phase) => tasksByPhase[phase] || []);
    const effectiveTasks = tasks.filter((task) => task.status !== "discarded");
    return {
      total: effectiveTasks.length,
      completed: effectiveTasks.filter((task) => task.status === "done").length,
      overdue: effectiveTasks.filter(
        (task) =>
          getTaskOverdueDays(task) > 0 ||
          getTaskOverdueContentCount(task) > 0
      ).length,
      reviewing: effectiveTasks.reduce(
        (total, task) => total + (task.subtask_summary.reviewing || 0),
        0
      ),
    };
  }, [tasksByPhase]);

  const matchedTaskCount = useMemo(
    () =>
      PROJECT_PHASES.reduce((total, phase) => {
        const phaseTasks = tasksByPhase[phase] || [];
        if (!normalizedSearchValue) return total + phaseTasks.length;
        return total + phaseTasks.filter((task) => {
          const assigneeText = task.assignees.map(getMemberName).join(" ");
          return [task.title, assigneeText, STATUS_LABELS[task.status]]
            .join(" ")
            .toLocaleLowerCase()
            .includes(normalizedSearchValue);
        }).length;
      }, 0),
    [getMemberName, normalizedSearchValue, tasksByPhase]
  );

  return (
    <Modal
      maskClosable={false}
      keyboard={false}
      className="projectTaskOverviewModal"
      closable={false}
      destroyOnClose
      footer={null}
      onCancel={onClose}
      open={open}
      style={{ top: 0, margin: 0, paddingBottom: 0 }}
      title={null}
      width="100vw"
    >
      <section className="projectTaskOverviewModal__shell">
        <header className="projectTaskOverviewModal__header">
          <div>
            <span>PROJECT OVERVIEW</span>
            <h2>{projectName}</h2>
            <p>四个阶段的任务模块汇总</p>
          </div>
          <div className="projectTaskOverviewModal__headerActions">
            <Button aria-label="关闭项目任务总览" icon={<CloseOutlined />} onClick={onClose} />
          </div>
        </header>

        <div className="projectTaskOverviewModal__summary" aria-label="项目任务汇总">
          <span><small>全部任务</small><strong>{summary.total}</strong></span>
          <span className="is-completed"><small>已完成</small><strong>{summary.completed}</strong></span>
          <span className="is-overdue"><small>存在逾期</small><strong>{summary.overdue}</strong></span>
          <span className="is-reviewing"><small>待审批子任务</small><strong>{summary.reviewing}</strong></span>
        </div>

        <div className="projectTaskOverviewModal__searchBar">
          <Input
            allowClear
            aria-label="搜索项目任务"
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="搜索任务标题、负责人或状态"
            prefix={<SearchOutlined />}
            value={searchValue}
          />
          <span>
            {normalizedSearchValue
              ? `找到 ${matchedTaskCount} 个任务`
              : "可按任务标题、负责人或状态筛选"}
          </span>
        </div>

        <div className="projectTaskOverviewModal__phaseGrid" aria-busy={loading}>
          {PROJECT_PHASES.map((phase) => {
            const phaseTasks = tasksByPhase[phase] || [];
            const effectivePhaseTasks = phaseTasks.filter(
              (task) => task.status !== "discarded"
            );
            const visibleTasks = normalizedSearchValue
              ? phaseTasks.filter((task) => {
                  const assigneeText = task.assignees.map(getMemberName).join(" ");
                  return [task.title, assigneeText, STATUS_LABELS[task.status]]
                    .join(" ")
                    .toLocaleLowerCase()
                    .includes(normalizedSearchValue);
                })
              : phaseTasks;
            const visibleTaskIds = new Set(
              visibleTasks.map((task) => task.id || task.title)
            );
            const phaseTaskMap = new Map(
              phaseTasks
                .filter((task) => task.id)
                .map((task) => [task.id!, task])
            );
            const dependencyLevels = groupTasksByDependencyLevel(phaseTasks)
              .map((level) => ({
                ...level,
                tasks: level.tasks.filter((task) =>
                  visibleTaskIds.has(task.id || task.title)
                ),
              }))
              .filter((level) => level.tasks.length > 0);
            const doneCount = effectivePhaseTasks.filter((task) => task.status === "done").length;
            const overdueCount = effectivePhaseTasks.filter(
              (task) =>
                getTaskOverdueDays(task) > 0 ||
                getTaskOverdueContentCount(task) > 0
            ).length;
            const phaseProgress = effectivePhaseTasks.length
              ? Math.round((doneCount / effectivePhaseTasks.length) * 100)
              : 0;
            return (
              <section className={`projectTaskOverviewModal__phase is-${phase}`} key={phase}>
                <header>
                  <div className="projectTaskOverviewModal__phaseHeading">
                    <span className="projectTaskOverviewModal__phaseIndex">
                      {PROJECT_PHASES.indexOf(phase) + 1}
                    </span>
                    <div>
                      <span>{PROJECT_PHASE_LABELS[phase]}</span>
                      <small><b>{doneCount}</b> / {effectivePhaseTasks.length} 已完成</small>
                    </div>
                  </div>
                  <div className="projectTaskOverviewModal__phaseActions">
                    <div className="projectTaskOverviewModal__phaseProgress" aria-label={`${PROJECT_PHASE_LABELS[phase]}完成进度 ${phaseProgress}%`}>
                      <i style={{ width: `${phaseProgress}%` }} />
                    </div>
                    <Button
                      aria-label={`进入${PROJECT_PHASE_LABELS[phase]}`}
                      icon={<RightOutlined />}
                      onClick={() => onOpenPhase(phase)}
                      type="text"
                    >
                      进入阶段
                    </Button>
                  </div>
                </header>
                {overdueCount > 0 ? (
                  <div className="projectTaskOverviewModal__phaseAlert">
                    <WarningOutlined /> {overdueCount} 个任务存在逾期项
                  </div>
                ) : null}
                <div className="projectTaskOverviewModal__taskList">
                  {loading ? (
                    <div className="projectTaskOverviewModal__empty">正在同步该阶段任务...</div>
                  ) : dependencyLevels.length ? dependencyLevels.map(({ level, tasks }) => (
                    <section className="projectTaskOverviewModal__taskLevel" key={level}>
                      <header>
                        <span>{level === 1 ? "起始任务" : `第 ${level} 层后续任务`}</span>
                        <small>{tasks.length} 个任务模块</small>
                      </header>
                      <div className="projectTaskOverviewModal__taskCards">
                        {tasks.map((task) => {
                    const taskOverdueDays = getTaskOverdueDays(task);
                    const overdueContentCount = getTaskOverdueContentCount(task);
                    const isOverdue = taskOverdueDays > 0 || overdueContentCount > 0;
                    const taskProgress = Math.max(
                      0,
                      Math.min(100, Math.round(task.subtask_summary.progress || 0))
                    );
                    const assigneeNames = task.assignees.map(getMemberName).filter(Boolean);
                    const predecessorTasks = (task.predecessor_ids || [])
                      .map((predecessorId) => phaseTaskMap.get(predecessorId))
                      .filter(Boolean) as ProjectTaskOverviewTask[];
                    const isSiblingTask = tasks.length > 1;
                    return (
                      <article
                        aria-label={`查看任务 ${task.title}`}
                        className={`projectTaskOverviewModal__task is-${task.status} ${
                          isOverdue ? "is-overdue" : ""
                        }`}
                        key={task.id || task.title}
                        onClick={() => onOpenTask(task)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onOpenTask(task);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className={`projectTaskOverviewModal__status is-${task.status} ${
                          isOverdue ? "is-overdue" : ""
                        }`}>
                          <span>{isOverdue ? <WarningOutlined /> : <i />}{isOverdue ? `逾期 ${taskOverdueDays} 天` : STATUS_LABELS[task.status]}</span>
                          <TaskPriorityTag priority={task.priority || "medium"} />
                        </div>
                        <strong className="projectTaskOverviewModal__taskName" title={task.title}>{task.title}</strong>
                        <div className="projectTaskOverviewModal__taskAssignees">
                          <span className="projectTaskOverviewModal__metaLabel"><UserOutlined />负责人</span>
                          <div>
                            {assigneeNames.length ? assigneeNames.map((name) => (
                              <span className="projectTaskOverviewModal__assignee" key={name} title={name}>
                                {name}
                              </span>
                            )) : <span className="projectTaskOverviewModal__unassigned">未分配</span>}
                          </div>
                        </div>
                        {(predecessorTasks.length || isSiblingTask) ? (
                          <div className="projectTaskOverviewModal__taskRelations">
                            {predecessorTasks.map((predecessor) => (
                              <Tag
                                icon={<LinkOutlined />}
                                key={predecessor.id || predecessor.title}
                                title={`前置任务：${predecessor.title}`}
                              >
                                前置 · {predecessor.title}
                              </Tag>
                            ))}
                            {isSiblingTask ? <Tag>同层并行</Tag> : null}
                          </div>
                        ) : null}
                        <div className="projectTaskOverviewModal__taskFooter">
                          {task.status === "discarded" ? (
                            <span>已废弃 · 不参与项目统计</span>
                          ) : <><span className={taskOverdueDays > 0 ? "is-overdue" : ""}>
                            <CalendarOutlined />
                            {taskOverdueDays > 0 ? `主任务逾期 ${taskOverdueDays} 天` : formatDate(task.due_time)}
                          </span>
                          {task.subtask_summary.total > 0 ? (
                            <span><CheckCircleFilled />{task.subtask_summary.completed} / {task.subtask_summary.total} 子任务</span>
                          ) : <span>未拆分子任务</span>}</>}
                        </div>
                        {task.status !== "discarded" && task.subtask_summary.total > 0 ? (
                          <div className="projectTaskOverviewModal__taskProgress">
                            <div>
                              <span>子任务进度</span>
                              <strong>{taskProgress}%</strong>
                              {overdueContentCount > 0 ? (
                                <em><WarningOutlined />{overdueContentCount} 项内容逾期</em>
                              ) : null}
                            </div>
                            <span><i style={{ width: `${taskProgress}%` }} /></span>
                          </div>
                        ) : null}
                      </article>
                    );
                        })}
                      </div>
                    </section>
                  )) : (
                    <div className="projectTaskOverviewModal__empty">
                      {normalizedSearchValue ? "该阶段没有匹配的任务" : "该阶段暂无任务模块"}
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </section>
    </Modal>
  );
};

export default ProjectTaskOverviewModal;
